import React, { useEffect, useState } from 'react';
import {
  Table,
  Typography,
  Popover,
  Button,
  Row,
  Image,
  Descriptions,
  DatePicker,
} from 'antd';
import * as moment from 'moment';
import { ReloadOutlined } from '@ant-design/icons';
import { MainLayout } from '../../components/Layout';
import { Admin } from '../../queries';
import { getApolloClient } from '../../clients/apollo';
import { getColumnSearchProps } from '../Listings/search-props';
const { RangePicker } = DatePicker;
const { Text } = Typography;

const TRANSACTION_STATUS_TYPES = {
  SUCCESS: { value: 'Success', color: '#52c41a' },
  FAIL: { value: 'Fail', color: '#faad14' },
  PENDING: { value: 'Pending', color: '#6e085c' },
};

const PAYMENT_METHODS_TYPES = {
  HYPER_PAY: 'Hyperpay',
};

const USER_ACCOUNT_TYPES = {
  INDIVIDUAL: 'Individual',
  REAL_ESTATE_OFFICE: 'Real Estate Office',
  DEVELOPMENT_COMPANY: 'Development Company',
  COMMERCIAL_COMPANY: 'Commercial Company',
};

const ORDER_TYPES = {
  CAMPAIGN: 'Campaign',
  BUNDLE: 'Bundle',
};

const CAMPAIGN_TYPES = {
  PRIME: 'Prime',
  FEATURED: 'Featured',
  ON_FACEBOOK: 'On Facebook',
};

const TRANSACTION_ORDER_TYPES = {
  CAMPAIGN: 'Campaign',
  BUNDLE: 'Bundle',
};

let client = null;

export default function Transactions(props) {
  const [isLoading, setLoading] = useState(false);
  const [Transactions, setTransactions] = useState([]);
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 10,
    total: 0,
    position: ['topLeft', 'bottomLeft'],
  });

  const getTransactions = async ({
    where = {},
    orderBy = { createdAt: 'desc' },
    skip = 0,
    take = 10,
    newPagination,
  }) => {
    try {
      setLoading(true);
      const transactionsResponse = await client.query({
        query: Admin.Transaction.GET_TRANSACTIONS,
        variables: {
          where,
          orderBy,
          skip,
          take,
        },
      });
      const transactionsCount = await client.query({
        query: Admin.Transaction.GET_TRANSACTIONS_COUNT,
        variables: {
          where,
        },
      });

      setPagination({
        ...newPagination,
        total: transactionsCount.data.transactionsConnection._count.id,
      });
      setTransactions(transactionsResponse.data.transactions);
    } catch (error) {
      console.log(
        '🚀 ~ file: Transactions.js ~ line 23 ~ getTransactions ~ error',
        error
      );
    } finally {
      setLoading(false);
    }
  };

  const handleTableChange = async (currentPagination, filters, sorter) => {
    const where = {};
    let orderBy = {};
    if (filters.category) {
      where.category = {
        in: filters.category,
      };
    }

    if (filters.source) {
      where.source = {
        equals: filters.source,
      };
    }

    if (filters.status) {
      where.status = {
        in: filters.status,
      };
    }

    if (filters.listingNo) {
      where.listingNo = {
        contains: filters.listingNo[0],
      };
    }

    if (filters.manualPromotion) {
      if (filters.manualPromotion[0]) {
        where.manualPromotion = {
          equals: true,
        };
      } else {
        where.OR = [
          {
            manualPromotion: {
              equals: false,
            },
          },
          {
            manualPromotion: {
              equals: null,
            },
          },
        ];
      }
    }

    if (sorter.field && sorter.order) {
      orderBy[sorter.field] = sorter.order === 'ascend' ? 'asc' : 'desc';
    } else {
      orderBy = {
        createdAt: 'desc',
      };
    }
    const take = currentPagination.pageSize;
    const skip = (currentPagination.current - 1) * currentPagination.pageSize;

    const newPagination = {
      ...pagination,
      ...currentPagination,
    };
    await getTransactions({ where, orderBy, take, skip, newPagination });
  };

  useEffect(() => {
    const { token } = props;
    client = getApolloClient({ token });
    getTransactions({});
  }, []);

  const paymentMethodPopOver = ({ paymentMethod }) => {
    return (
      <Table
        key={`${paymentMethod.id}-payment-method-pop-over`}
        columns={[
          {
            title: 'Name',
            dataIndex: 'name',
            key: 'name-paymentMethod',
          },
          {
            title: 'Type',
            dataIndex: 'type',
            key: 'type-paymentMethod',
            render: (type) => PAYMENT_METHODS_TYPES[type],
          },
          {
            title: 'Status',
            dataIndex: 'isActive',
            key: 'status-paymentMethod',
            render: (isActive) => (isActive ? 'Enabled' : 'Disabled'),
          },
          {
            title: 'Id',
            dataIndex: 'id',
            key: 'id-paymentMethod',
          },
        ]}
        dataSource={[paymentMethod]}
        size="small"
        pagination={false}
        bordered
        rowKey={(record) => `payment-method-pop-over-${record.id}`}
      />
    );
  };

  const userPopOver = ({ user }) => {
    return (
      <Table
        key={`${user.id}-user-pop-over`}
        columns={[
          {
            title: 'Name',
            dataIndex: 'name',
            key: 'name-user',
          },
          {
            title: 'Account Type',
            dataIndex: 'accountType',
            key: 'accountType-user',
            render: (accountType) => USER_ACCOUNT_TYPES[accountType],
          },
          {
            title: 'Photo',
            dataIndex: 'photo',
            key: 'photo-user',
            render: (photo) => {
              return (
                <Image
                  preview={false}
                  width={200}
                  height={200}
                  src={`${process.env.REACT_APP_IMAGES_BASE_URL}/users/${user.id}/${photo}`}
                  fallback="data:image/png;base64,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"
                />
              );
            },
          },
          {
            title: 'Role',
            dataIndex: 'role',
            key: 'role-user',
            render: (role) => (role === 'ADMIN' ? 'Admin' : 'User'),
          },
          {
            title: 'Id',
            dataIndex: 'id',
            key: 'id-user',
          },
        ]}
        dataSource={[user]}
        size="small"
        pagination={false}
        bordered
        rowKey={(record) => `user-pop-over-${record.id}`}
      />
    );
  };

  const orderedBundleInfo = ({
    orderedBundle: {
      id,
      bundle: { id: bundleId, listingsCreateLimit, price, isActive },
    },
  }) => {
    return (
      <Descriptions title="" bordered size="small">
        <Descriptions.Item label="Ordered Campaign Id" span={3}>
          {id}
        </Descriptions.Item>
        <Descriptions.Item label="Bundle Id" span={3}>
          {bundleId}
        </Descriptions.Item>

        <Descriptions.Item label="Price" span={3}>
          JOD{price}
        </Descriptions.Item>

        <Descriptions.Item label="Status" span={3}>
          {isActive ? 'Enabled' : 'Disabled'}
        </Descriptions.Item>

        <Descriptions.Item label="Create Limit" span={3}>
          {listingsCreateLimit} listings
        </Descriptions.Item>
      </Descriptions>
    );
  };

  const orderedCampaignInfo = ({
    orderedCampaign: {
      id,
      campaign: {
        id: campaignId,
        type,
        price,
        isActive,
        validForDays,
        description,
      },
    },
  }) => {
    return (
      <Descriptions title={CAMPAIGN_TYPES[type]} bordered size="small" key={id}>
        <Descriptions.Item label="Ordered Campaign Id" span={3}>
          {id}
        </Descriptions.Item>
        <Descriptions.Item label="Campaign Id" span={3}>
          {campaignId}
        </Descriptions.Item>

        <Descriptions.Item label="Type" span={3}>
          {CAMPAIGN_TYPES[type]}
        </Descriptions.Item>

        <Descriptions.Item label="Price" span={3}>
          JOD{price}
        </Descriptions.Item>

        <Descriptions.Item label="Status" span={3}>
          {isActive ? 'Enabled' : 'Disabled'}
        </Descriptions.Item>

        <Descriptions.Item label="Valid For Days" span={3}>
          {validForDays}
        </Descriptions.Item>

        <Descriptions.Item label="Description" span={3}>
          {description}
        </Descriptions.Item>
      </Descriptions>
    );
  };

  const orderPopOver = ({ order }) => {
    return (
      <Table
        key={`${order.id}-order-pop-over`}
        columns={[
          {
            title: 'Type',
            dataIndex: 'type',
            key: 'type-order',
            render: (type) => ORDER_TYPES[type],
          },
          {
            title: 'Total Price',
            dataIndex: 'totalPrice',
            key: 'totalPrice-order',
            render: (totalPrice) => `JOD${totalPrice}`,
          },
          {
            title: 'Status',
            dataIndex: 'isDeleted',
            key: 'isDeleted',
            render: (isDeleted) => {
              return (
                <Text
                  style={{
                    color: isDeleted ? '#faad14' : '#52c41a',
                  }}
                >
                  {isDeleted ? 'Declined' : 'Accepted'}
                </Text>
              );
            },
          },
          {
            title: 'Ordered Bundle',
            dataIndex: 'orderedBundle',
            key: 'orderedBundle-order',
            render: (orderedBundle) =>
              orderedBundle ? orderedBundleInfo({ orderedBundle }) : '',
          },
          {
            title: 'Ordered Campaigns',
            dataIndex: 'orderedCampaigns',
            key: 'orderedCampaign-order',
            render: (orderedCampaigns) => {
              return orderedCampaigns.length > 0
                ? orderedCampaigns.map((orderedCampaign) =>
                    orderedCampaignInfo({ orderedCampaign })
                  )
                : null;
            },
          },
          {
            title: 'Id',
            dataIndex: 'id',
            key: 'id-order',
          },
        ]}
        dataSource={[order]}
        size="small"
        pagination={false}
        bordered
        rowKey={(record) => `order-pop-over-${record.id}`}
      />
    );
  };

  return (
    <MainLayout title="Transactions">
      <Row>
        <Button
          type="primary"
          onClick={() => {
            getTransactions({});
          }}
          icon={<ReloadOutlined />}
        >
          Reload
        </Button>
      </Row>
      <Row style={{ margin: '10px 0' }}>
        <RangePicker
          onChange={(dates) => {
            if (dates && dates.length === 2) {
              const formattedStartDate = dates[0].format('YYYY-MM-DD');
              const formattedEndDate = dates[1].format('YYYY-MM-DD');
              getTransactions({
                where: {
                  createdAt: {
                    gte: formattedStartDate,
                    lte: formattedEndDate,
                  },
                },
              });
            }
          }}
        />
      </Row>
      <Table
        columns={[
          {
            title: 'Amount',
            dataIndex: 'amount',
            key: 'amount',
            render: (amount) => `JOD${amount}`,
            sorter: (a, b) => a.amount - b.amount,
          },
          {
            title: 'Manual Promotion',
            dataIndex: 'manualPromotion',
            key: 'manualPromotion',
            render: (manualPromotion) => (manualPromotion ? 'Yes' : 'No'),
            filters: [
              {
                text: 'Yes',
                value: true,
              },
              {
                text: 'No',
                value: false,
              },
            ],
            filterMultiple: false,
            onFilter: (value, row) => {
              return value === true
                ? row.manualPromotion === value
                : row.manualPromotion === value || row.manualPromotion === null;
            },
          },
          {
            title: 'Status',
            dataIndex: 'status',
            key: 'status',
            render: (status) => {
              return (
                <Text
                  style={{
                    color: TRANSACTION_STATUS_TYPES[status].color,
                  }}
                >
                  {TRANSACTION_STATUS_TYPES[status].value}
                </Text>
              );
            },
            sorter: (a, b) => a.status.length - b.status.length,
            filters: [
              {
                text: 'Success',
                value: 'SUCCESS',
              },
              {
                text: 'Fail',
                value: 'FAIL',
              },
              {
                text: 'Pending',
                value: 'PENDING',
              },
            ],
            filterMultiple: false,
            onFilter: (value, row) => row.status.indexOf(value) === 0,
          },
          {
            title: 'Type',
            key: 'type',
            render: (_, row) => {
              return (
                <Text>
                  {
                    TRANSACTION_ORDER_TYPES[
                      row.order.orderedBundle ? 'BUNDLE' : 'CAMPAIGN'
                    ]
                  }
                </Text>
              );
            },
          },
          {
            title: 'Listing Number',
            key: 'listingNo',
            ...getColumnSearchProps(
              'listingNo',
              (selectedKeys, confirm) => confirm(),
              (clearFilters, dataIndex) => clearFilters(),
              Transactions
            ),
            render: (row) =>
              row.listingNo ||
              (row.order &&
                row.order.type === 'CAMPAIGN' &&
                row.order.orderedCampaigns &&
                row.order.orderedCampaigns[0] &&
                row.order.orderedCampaigns[0].listing &&
                row.order.orderedCampaigns[0].listing.no),
          },
          {
            title: 'Commercial Ad Number',
            key: 'commercial-ad-number',
            render: (row) =>
              row.order &&
              row.order.type === 'CAMPAIGN' &&
              row.order.orderedCampaigns &&
              row.order.orderedCampaigns[0] &&
              row.order.orderedCampaigns[0].commercialAd &&
              row.order.orderedCampaigns[0].commercialAd.no,
          },
          {
            title: 'User Name',
            dataIndex: 'user',
            key: 'user-name',
            render: (user) => user.name,
          },
          {
            title: 'Payment Method',
            dataIndex: 'paymentMethod',
            key: 'payment-method',
            render: (paymentMethod) => {
              return (
                <Row
                  style={{
                    justifyContent: 'center',
                  }}
                >
                  <Popover
                    key={`${paymentMethod.id}-payment-method-pop-over-main`}
                    content={paymentMethodPopOver({ paymentMethod })}
                    title="Payment Method Info"
                    trigger="click"
                    arrowPointAtCenter
                    placement="bottom"
                  >
                    <Button>Show</Button>
                  </Popover>
                </Row>
              );
            },
          },
          {
            title: 'User',
            dataIndex: 'user',
            key: 'user',
            render: (user) => {
              return (
                <Row
                  style={{
                    justifyContent: 'center',
                  }}
                >
                  <Popover
                    key={`${user.id}-user-pop-over-main`}
                    content={userPopOver({ user })}
                    title="User Info"
                    trigger="click"
                    arrowPointAtCenter
                    placement="bottom"
                  >
                    <Button>Show</Button>
                  </Popover>
                </Row>
              );
            },
          },
          {
            title: 'Order',
            dataIndex: 'order',
            key: 'order',
            render: (order) => {
              return (
                <Row
                  style={{
                    justifyContent: 'center',
                  }}
                >
                  <Popover
                    key={`${order.id}-order-pop-over-main`}
                    content={orderPopOver({ order })}
                    title="Order Info"
                    trigger="click"
                    arrowPointAtCenter
                    placement="bottom"
                  >
                    <Button>Show</Button>
                  </Popover>
                </Row>
              );
            },
          },
          {
            title: 'Created at',
            dataIndex: 'createdAt',
            key: 'createdAt',
            render: (createdAt) => moment(createdAt).format('L'),
            sorter: (a, b) => {
              if (!a.createdAt || !b.createdAt) return false;
              const diff = moment(a.createdAt).diff(
                moment(b.createdAt),
                'seconds'
              );
              return diff;
            },
          },
          {
            title: 'Updated at',
            dataIndex: 'updatedAt',
            key: 'updatedAt',
            render: (updatedAt) => moment(updatedAt).format('L'),
            sorter: (a, b) => {
              if (!a.updatedAt || !b.updatedAt) return false;
              const diff = moment(a.updatedAt).diff(
                moment(b.updatedAt),
                'seconds'
              );
              return diff;
            },
          },
          {
            title: 'Id',
            dataIndex: 'id',
            key: 'id',
          },
        ]}
        rowKey={(record) => `main-table-transactions-${record.id}`}
        loading={isLoading}
        pagination={pagination}
        dataSource={Transactions}
        bordered
        scroll={{ x: true }}
        onChange={handleTableChange}
      />
    </MainLayout>
  );
}
